/* eslint-disable @typescript-eslint/no-explicit-any */
import { MutationKey, NetworkMode } from '@tanstack/react-query';

type RetryOption = boolean | number | ((failureCount: number, error: any) => boolean);

export interface MutationOptions {
  mutationKey: MutationKey;
  mutationFn: any;
  onMutate?: (variables: any) => Promise<any | undefined> | any | undefined;
  onSuccess?: (data: any, variables: any, context: any) => Promise<any> | any;
  onError?: (err: any, variables?: any, context?: any) => void;
  onSettled?: (data: any, error: any | null, variables?: any, context?: any) => void;
  networkMode: NetworkMode;
  retry: RetryOption;
  retryDelay?: number | ((failureCount: number, error: any) => number);
}

/** The http-client rejects a response-less failure as `HTTP -1: ...` (api/http-client.ts). */
export function isUnreachableError(error: unknown): boolean {
  if (typeof error === 'string') return error.includes('HTTP -1');
  const status = (error as { status?: number } | null)?.status;
  return typeof status === 'number' && status <= 0;
}

const MAX_SERVER_ERROR_RETRIES = 5;

export const defaultMutationOptions: {
  networkMode: NetworkMode;
  retry: RetryOption;
} = {
  networkMode: 'online',
  // Exhausting retries clears `isPaused`, which drops the mutation from persistence and
  // from resumePausedMutations. Never exhaust on a connectivity failure.
  retry: (failureCount: number, error: unknown) =>
    isUnreachableError(error) ? true : failureCount < MAX_SERVER_ERROR_RETRIES,
};

// Options specific for mutations that need to work offline
export const offlineFirstMutationOptions: {
  networkMode: NetworkMode;
  retry: boolean | number;
} = {
  networkMode: 'offlineFirst' as NetworkMode,
  retry: 3,
};
